import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client.ts';
import { queryDefaults } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';

/**
 * `/admin/clio-counts` — R3-R5, the Clio counts (docs-comms plan 2.4; Arthur, 16 October 2026). Tiers 1-2.
 * One button runs them in the API, reading Clio with GET only and the app's own identity; the page shows
 * the step it is on and, when done, the report, which is counts only: no name, matter number, subject or
 * filename can be in it (api/src/clio/counts.ts). The report is also written to the app log.
 */
interface CountsState {
  running: boolean;
  step: string | null;
  startedAt: string | null;
  report: Record<string, unknown> | null;
  failure: string | null;
  grantedBy: string;
}

export function ClioCounts() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['clio-counts'],
    queryFn: () => apiFetch<CountsState>('/admin/clio/counts', getToken),
    ...queryDefaults,
    refetchInterval: (query) => (query.state.data?.running ? 5000 : false),
  });
  const run = useMutation({
    mutationFn: () => apiFetch<CountsState>('/admin/clio/counts/run', getToken, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clio-counts'] }),
  });
  const d = q.data;
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-4xl px-4 py-6">
        <h1 className="title-bar">Clio counts (R3-R5)</h1>
        <p className="mt-1 text-neutral-700">
          Counts only, read from Clio with the grant made on 16 October ({d?.grantedBy ?? 'Clio'}). Nothing in Clio changes, and nothing
          that names a person or a matter is kept. A run takes from minutes to about an hour, depending on Clio’s limits.
        </p>
        {q.error && !d ? (
          <LoadFailure what="The Clio counts" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
        ) : !d ? (
          <p className="py-6 text-neutral-600">Loading…</p>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button type="button" disabled={d.running || run.isPending} onClick={() => run.mutate()}
                      className="min-h-[44px] rounded-lg bg-neutral-900 px-4 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600">
                {d.running ? 'Running…' : 'Run the Clio counts'}
              </button>
              {d.running && <span className="text-neutral-800">On: {d.step ?? 'starting'} (since {d.startedAt?.slice(11, 16)} UTC)</span>}
            </div>
            {run.error && <p role="alert" className="mt-2 text-warning-fg">{run.error.message}</p>}
            {d.failure && <p role="alert" className="mt-3 rounded border border-warning-edge bg-warning-bg px-3 py-2 text-warning-fg">{d.failure}</p>}
            {d.report && (
              <section className="mt-4">
                <h2 className="text-sm font-bold tracking-[0.08em] text-neutral-600 uppercase">The last report</h2>
                <pre className="mt-1 max-h-[70vh] overflow-auto rounded-lg border border-line bg-panel p-3 text-sm text-neutral-900">
                  {JSON.stringify(d.report, null, 2)}
                </pre>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
